import { theme } from 'module/ui/styles/theme';
import { Radio } from 'antd';
import { FC, ComponentProps, ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { ifProp, prop } from 'styled-tools';

type SwitcherProps = Omit<
  ComponentProps<typeof Radio.Group>,
  'size' | 'buttonStyle'
> & {
  size?: keyof typeof theme.heights;
  solid?: boolean;
  activeColor?: string;
  hoverBgColor?: string;
};

type ButtonProps = ComponentProps<typeof Radio.Button> & {
  icon?: ReactNode;
};

const Icon = styled.div<{ iconWithChildren? }>`
  display: inline-flex;
  align-items: center;
`;
const Wrapper = styled.span<{ hasIconOnly: boolean }>`
  display: inline-flex;
  align-items: center;
  ${ifProp(
    'hasIconOnly',
    css`
      ${Icon} {
        margin-right: var(--spacing-03);
      }
    `,
  )};
`;
const ButtonInternal: FC<React.PropsWithChildren<ButtonProps>> = ({
  icon,
  children,
  ...props
}) => (
  <Radio.Button {...props} key={props.key as React.Key | undefined}>
    <Wrapper hasIconOnly={!!children}>
      {icon == null ? null : <Icon>{icon}</Icon>}
      {children}
    </Wrapper>
  </Radio.Button>
);

const Button = styled(ButtonInternal)``;

const SwitcherInternal: FC<React.PropsWithChildren<SwitcherProps>> = ({
  size,
  solid,
  ...props
}) => (
  <Radio.Group
    buttonStyle={solid ? 'solid' : 'outline'}
    {...props}
    key={props.key as React.Key | undefined}
  />
);

const SwitcherComponent = styled(SwitcherInternal)`
  display: flex;
  padding: var(--spacing-02);
  background-color: var(--scarlet-gray-20);
  border-radius: var(--border-radius-small);

  ${Button} {
    padding: 0 var(--spacing-03);
    height: ${({ size = 'medium', theme: { heights } }) => heights[size]};
    line-height: ${({ size = 'medium', theme: { heights, pixels } }) =>
      `${pixels(heights[size]) - 2}px`};
    display: flex;
    border: none;
    border-radius: var(--border-radius-base);
    background-color: transparent;
    &.ant-radio-button-wrapper:not(:first-child)::before {
      background-color: transparent;
    }
    .ant-radio-button + span {
      display: flex;
    }

    &:last-child {
      margin-left: var(--spacing-02);
    }

    .ant-radio-button-wrapper:first-child {
      border: none;
    }

    &:hover {
      color: ${prop('activeColor', 'var(--black-100)')};
      background-color: ${prop('hoverBgColor', 'var(--white-100)')};
    }

    &.ant-radio-button-wrapper-checked:not(.ant-radio-button-wrapper-disabled) {
      color: var(--black-100);
      background-color: var(--white-100);
      border-color: var(--white-100);
    }
    &.ant-radio-button-wrapper-checked:not(
        .ant-radio-button-wrapper-disabled
      )::before {
      background-color: ${prop('activeColor', 'transparent')};
    }
  }
`;

type Switcher = typeof SwitcherComponent & {
  Button: typeof Button;
};

const Switcher: Switcher = SwitcherComponent as any;

Switcher.Button = Button;

export { Switcher };
